import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import { YearCalendar } from "@/components/ActivityCalendar";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import i18n from "@/i18n";
import { addMonths, formatMonth, getMonthFromDate, getYearFromDate, setYearAndMonth } from "@/lib/calendar-utils";
import type { MonthNavigatorProps } from "@/types/statistics";

export const MonthNavigator = ({ visibleMonth, onMonthChange, activityStats, holidays }: MonthNavigatorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentMonth = new Date(visibleMonth);
  const currentYear = getYearFromDate(visibleMonth);
  const currentMonthNum = getMonthFromDate(visibleMonth);

  const handlePrevMonth = () => {
    onMonthChange(addMonths(visibleMonth, -1));
  };

  const handleNextMonth = () => {
    onMonthChange(addMonths(visibleMonth, 1));
  };

  const handleDateClick = (date: string) => {
    onMonthChange(formatMonth(date));
    setIsOpen(false);
  };

  const handleYearChange = (year: number) => {
    onMonthChange(setYearAndMonth(year, currentMonthNum));
  };

  return (
    <div className="w-full flex items-center justify-between gap-2">
      {/* Month selector with enhanced styling */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <button className="group relative px-3 py-1.5 -ml-3 rounded-xl transition-all duration-300 flex items-center gap-2 select-none">
            {/* Background gradient on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10 dark:from-amber-500/5 dark:to-orange-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Month label */}
            <span className="relative text-sm font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
              {currentMonth.toLocaleString(i18n.language, { year: "numeric", month: "long" })}
            </span>

            {/* Chevron with animation */}
            <ChevronDownIcon className="relative w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground transition-all duration-300 group-hover:translate-y-0.5" />
          </button>
        </DialogTrigger>
        <DialogContent className="p-0 border-none bg-background md:max-w-4xl" size="2xl" showCloseButton={false}>
          <DialogTitle className="sr-only">Select Month</DialogTitle>
          <YearCalendar
            selectedYear={currentYear}
            data={activityStats}
            onYearChange={handleYearChange}
            onDateClick={handleDateClick}
            holidays={holidays}
          />
        </DialogContent>
      </Dialog>

      {/* Navigation buttons with enhanced styling */}
      <div className="flex items-center gap-1">
        <button
          className="group relative p-2 rounded-lg overflow-hidden transition-all duration-300"
          onClick={handlePrevMonth}
          aria-label="Previous month"
        >
          {/* Background gradient on hover */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent dark:from-amber-500/5 dark:to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Icon container with glow */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-50 dark:from-amber-950/10 dark:to-amber-950/5 rounded-md blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <ChevronLeftIcon className="relative w-4 h-4 text-muted-foreground group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-all duration-300 group-hover:-translate-x-0.5" />
          </div>
        </button>

        <button
          className="group relative p-2 rounded-lg overflow-hidden transition-all duration-300"
          onClick={handleNextMonth}
          aria-label="Next month"
        >
          {/* Background gradient on hover */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent dark:from-amber-500/5 dark:to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Icon container with glow */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-50 dark:from-amber-950/10 dark:to-amber-950/5 rounded-md blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <ChevronRightIcon className="relative w-4 h-4 text-muted-foreground group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-all duration-300 group-hover:translate-x-0.5" />
          </div>
        </button>
      </div>
    </div>
  );
};
